import React, { FC, useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { toVWMobile, toVWDesktop, toREM } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import Marquee from "react-fast-marquee";
import { shuffle } from "lodash";
import Link from "../../components/shared/Link";
import ButtonLink, { ButtonType } from "../shared/ButtonLink";
import useRightToLeft from "../../hooks/useRightToLeft";

export type OurImpactProps = {
  isPublished?: boolean;
  title?: string;
  description?: string;
  button: { label: string; link: string };
  benefitList: Benefit[];
  type: string;
  backgroundColor: string;
  staticImages: Image[];
};

type Benefit = {
  label_1: string;
  label_2: string;
};

type Image = {
  link: string;
  src: string;
  alt: string;
};

const OurImpact: FC<OurImpactProps> = ({
  isPublished,
  title,
  description,
  button,
  benefitList,
  backgroundColor,
  staticImages,
  type,
}: OurImpactProps) => {
  if (!isPublished) return null;

  const isRLT = useRightToLeft();

  const missionStyles = {
    padding: 300,
    fontColor: "var(--color-green-30)",
    button: ButtonType.PRIMARY_LIGHT,
  };

  const impactStyles = {
    padding: 300,
    fontColor: "var(--color-green-90)",
    button: ButtonType.PRIMARY_DARK,
  };

  const largeStyles = {
    padding: 125,
    fontColor: "var(--color-white)",
    button: ButtonType.PRIMARY_LIGHT,
  };

  const [partnersArrayToUse, setPartnersArray] = useState<Image[]>([]);

  const duplicatePartners = (partnersShuffled: Image[] = []) => {
    return [...partnersShuffled, ...partnersShuffled];
  };

  const styles = useMemo(() => {
    if (type !== null && type === "type1") {
      return impactStyles;
    }
    if (type !== null && type === "large_text") {
      return largeStyles;
    }
    return missionStyles;
  }, [type]);

  useEffect(() => {
    const arrayShuffled = shuffle(staticImages);
    const partnersShuffleDuplicate = duplicatePartners(arrayShuffled);
    setPartnersArray(partnersShuffleDuplicate);
  }, [staticImages]);

  const hasTopContent = title || description || (button?.link && button?.label);

  return (
    <Container className="bg-color-linen" backgroundColor={backgroundColor}>
      {hasTopContent && (
        <TextContainer padding={styles.padding}>
          {title && (
            <Title
              dangerouslySetInnerHTML={{
                __html: title,
              }}
            />
          )}
          {description && (
            <Description
              isRTL={isRLT}
              margin={button?.link && button?.label ? 70 : 2}
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          {button?.link && button?.label && (
            <ButtonLink
              className="button"
              link={button?.link}
              label={button?.label}
              type={styles.button}
            />
          )}
        </TextContainer>
      )}
      {staticImages?.length ? (
        <PartnersContainer>
          <Marquee direction={isRLT ? "right" : "left"} gradient={false}>
            {partnersArrayToUse.map((partner, key) => (
              <Link to={partner?.link} key={partner?.alt + key}>
                <Partner src={partner?.src} alt={partner?.alt} />
              </Link>
            ))}
          </Marquee>
        </PartnersContainer>
      ) : (
        <StyledMarquee
          className="ignore"
          gradient={false}
          play={true}
          speed={40}
        >
          <BenefitWrapper bigPadding={!!hasTopContent}>
            {benefitList?.map((benefit, index) => (
              <Benefit key={index}>
                <BenefitPercentage
                  className="font-gt-alpina text-color-dark-green"
                  fontColor={styles.fontColor}
                >
                  {benefit?.label_1}
                </BenefitPercentage>
                <BenefitName
                  className="font-gt-alpina  text-color-dark-green"
                  fontColor={styles.fontColor}
                >
                  {benefit?.label_2}
                </BenefitName>
              </Benefit>
            ))}
          </BenefitWrapper>
        </StyledMarquee>
      )}
    </Container>
  );
};

const Container = styled.section<{
  backgroundColor: string;
}>`
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "var(--color-green-90)"};
  padding: ${toVWMobile(52)}vw 0 ${toVWMobile(57)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${toVWDesktop(95)}vw 0 ${toVWDesktop(72)}vw;
  }
`;

const StyledMarquee = styled(Marquee)`
  direction: ltr;
  overflow-y: hidden;
`;

const TextContainer = styled.div<{ padding?: number }>`
  padding: 0 ${toVWMobile(24)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${(props) =>
      props.padding
        ? `0 ${toVWDesktop(props.padding)}vw`
        : `0 ${toVWDesktop(300)}vw`};
  }
`;

const Title = styled.h3<{ fontColor?: string }>`
  color: ${(props) => props.fontColor};
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(48)}rem;

  font-style: normal;
  letter-spacing: ${toREM(1.44)}rem;
  line-height: ${toVWMobile(56)}vw;
  margin-bottom: ${toVWMobile(15)}vw;
  text-align: center;
  text-transform: uppercase;

  &,
  p,
  span {
    font-weight: 900;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(56)}rem;
    letter-spacing: ${toREM(1.68)}rem;
    line-height: ${toVWDesktop(64)}vw;
    margin-bottom: ${toVWDesktop(15)}vw;
  }
`;

const Description = styled.p<{
  fontColor?: string;
  isRTL?: boolean;
  margin?: number;
}>`
  color: ${(props) => props.fontColor};
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(18)}rem;
  font-weight: 600;
  font-style: normal;
  letter-spacing: normal;
  line-height: ${toVWMobile(28)}vw;
  margin-bottom: ${({ margin }) => toVWMobile(margin ?? 70)}vw;
  text-align: ${({ isRTL }) => (isRTL ? "right" : "left")};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    line-height: ${toVWDesktop(24)}vw;
    margin-bottom: ${toVWDesktop(40)}vw;
    text-align: center;
  }
`;

const BenefitWrapper = styled.div<{ bigPadding?: boolean }>`
  display: flex;
  padding-top: ${({ bigPadding }) =>
    bigPadding ? toVWMobile(61) : toVWMobile(0)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${({ bigPadding }) =>
      bigPadding ? toVWDesktop(81) : toVWDesktop(22)}vw;
    padding-bottom: ${({ bigPadding }) => (bigPadding ? 0 : toVWDesktop(16))}vw;
  }
`;

const Benefit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 ${toVWMobile(31)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: 0 ${toVWDesktop(52)}vw;
  }
`;

const BenefitPercentage = styled.p<{ fontColor: string }>`
  color: ${(props) => props.fontColor};
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(80)}rem;
  font-weight: 900;
  font-style: normal;
  letter-spacing: ${toREM(2.4)}rem;
  line-height: ${toVWMobile(70)}vw;
  text-align: center;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(120)}rem;
    letter-spacing: ${toREM(4.8)}rem;
    line-height: ${toVWDesktop(80)}vw;
    margin-bottom: ${toVWDesktop(10)}vw;
  }
`;

const BenefitName = styled.p<{ fontColor: string }>`
  color: ${(props) => props.fontColor};
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(24)}rem;
  font-weight: 900;
  font-style: normal;
  letter-spacing: ${toREM(1.44)}rem;
  line-height: normal;
  text-align: center;
  text-transform: uppercase;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(36)}rem;
    letter-spacing: ${toREM(1.08)}rem;
    line-height: ${toVWDesktop(40)}vw;
  }
`;

const PartnersContainer = styled.div`
  direction: ltr;
  padding: ${toVWMobile(53)}vw 0 ${toVWMobile(60)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${toVWDesktop(68)}vw 0 ${toVWDesktop(82.2)}vw;
  }
`;

const Partner = styled.img`
  width: ${toVWMobile(162)}vw;
  height: ${toVWMobile(162)}vw;
  margin-left: ${toVWMobile(26)}vw;
  object-fit: cover;
  display: inline-block;
  background-color: var(--color-white);
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(220)}vw;
    height: ${toVWDesktop(220)}vw;
    margin-left: ${toVWDesktop(36)}vw;
  }
`;

export default OurImpact;
